<script setup lang="ts">

import { ECOMMERCE_ITEM_PRICE_TYPE, type EcommerceItem } from '@/types/shared/player-config'
import CoinIcon from '@/components/icons/CoinIcon.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { T } from '@tolgee/vue'
import ThePremium from '@/components/ThePremium.vue'
import { useEcommerceItemDetailsPurchase } from '@/composables/game/ecommerce'
import { formatNumberFullTruncate, formatUnshortenedNumber } from '@/helpers/utils'
import TonDiscountBadge from '@/components/TonDiscountBadge.vue'
import ShopCard2 from '@/components/ShopCard2.vue'
import ShopCard from '@/components/ShopCard.vue'
import { ref } from 'vue'
import { useConfetti } from '@/composables/game/confetti'
import { twMerge } from 'tailwind-merge'

const props = withDefaults(defineProps<{
	item: EcommerceItem
	image?: string
	coinsToEarn?: number
	cardStyle?: 'default' | '2'
	cardOuterStyle?: string
	showName?: boolean
	hideAfterPurchase?: boolean
	tonDiscountBadgeClass?: string
}>(), {
  cardStyle: 'default',
  coinsToEarn: undefined,
  image: undefined,
  cardOuterStyle: undefined,
  showName: false,
  hideAfterPurchase: true,
  tonDiscountBadgeClass: '',
})

const emit = defineEmits<{
	(e: 'hide'): void
}>()

const {
  starPrice,
  tonPrice,
  isBuyingWithStars,
  isBuyingWithTon,
  showTonPurchase,
  onPurchase,
} = useEcommerceItemDetailsPurchase(props.item)
const { showConfetti } = useConfetti()

const purchaseDone = ref(false)

const coins = props.coinsToEarn

async function _onPurchase(priceType: ECOMMERCE_ITEM_PRICE_TYPE) {
  const isPaid = await onPurchase(priceType)
  if (isPaid) {
    purchaseDone.value = true
    if (props.hideAfterPurchase) {
      setTimeout(() => {
        emit('hide')
      }, priceType === ECOMMERCE_ITEM_PRICE_TYPE.STAR ? 350 : 1)
    } else {
      showConfetti()
    }
  }
}
</script>

<template>
  <div class="w-full flex flex-col items-center">
    <img
      v-if="image"
      class="h-[120px] w-auto object-cover"
      :src="image"
    >
    <div
      v-else
      class="relative h-[117px] w-auto"
    >
      <ShopCard
        v-if="cardStyle === 'default'"
        :item="item"
        only-visual
        :style="cardOuterStyle"
      />
      <ShopCard2
        v-else
        :item="item"
        only-visual
        :style="cardOuterStyle"
      />
    </div>
    <div
      v-if="coins"
      class="mt-6 flex items-center gap-x-3"
      v-tooltip="{content: formatUnshortenedNumber(coins), placement: 'bottom'}"
    >
      <CoinIcon class="h-10 w-auto min-w-[26px]" />
      <div class="text-light-white text-[32px] leading-10 font-bold">
        {{ formatNumberFullTruncate(coins) }}
      </div>
    </div>
    <template v-if="showName || !hideAfterPurchase">
      <template v-if="!hideAfterPurchase && purchaseDone">
        <div
          class="mt-6 flex text-light-white text-[24px] leading-8 font-bold -tracking-[0.48px]"
        >
          Purchase successful!
        </div>
        <div class="w-full text-center text-white/60 text-base font-medium">
          {{ item.name }} = ${{ formatNumberFullTruncate(starPrice.usdAmount, true) }}
        </div>
      </template>
      <div
        v-else-if="showName"
        class="mt-3 text-center flex text-light-white text-[24px] leading-8 font-bold -tracking-[0.48px]"
      >
        <span>
          {{ item.name }} = ${{ formatNumberFullTruncate(starPrice.usdAmount, true) }}
        </span>
      </div>
    </template>

    <template v-if="hideAfterPurchase || !purchaseDone">
      <BaseButton
        class="mt-6 w-full"
        :loading="isBuyingWithStars"
        @click="_onPurchase(ECOMMERCE_ITEM_PRICE_TYPE.STAR)"
      >
        <span
          v-if="!isBuyingWithStars"
          class="flex items-center gap-x-2"
        >
          <T
            key-name="starter_pack.buy_with"
            default-value="Buy with"
          />
          <ThePremium :with-tooltip="false" />
          <span class="-ml-0.5">{{ formatUnshortenedNumber(starPrice.amount) }}</span>
        </span>
        <T
          v-else
          key-name="lingo.giveaway.transaction.loading_cta"
          default-value="Processing..."
        />
      </BaseButton>

      <template v-if="showTonPurchase">
        <div class="relative w-full">
          <div :class="twMerge('absolute right-12 top-1 rotate-[20deg]', tonDiscountBadgeClass)">
            <TonDiscountBadge label="-10%" />
          </div>
          <BaseButton
            style-type="tertiary"
            class="mt-3 w-full"
            :loading="isBuyingWithTon"
            @click="_onPurchase(ECOMMERCE_ITEM_PRICE_TYPE.TON)"
          >
            <span
              v-if="!isBuyingWithTon"
              class="flex items-center gap-x-2"
            >
              <T
                key-name="starter_pack.buy_with"
                default-value="Buy with"
              />
              <img
                src="@/assets/images/ton-symbol.png"
                class="size-5 min-w-[20px] object-cover"
              >
              <span>{{ tonPrice?.amount }}</span>
            </span>
            <T
              v-else
              key-name="lingo.giveaway.transaction.loading_cta"
              default-value="Processing..."
            />
          </BaseButton>
        </div>

        <div class="mx-auto mt-2 max-w-[307px] text-center text-sm -tracking-[0.28px] text-white/[.87] font-medium">
          <T
            key-name="transaction.process_description"
            default-value="Transaction may take a few minutes to process. You’ll be notified once it’s successful."
          />
        </div>
      </template>
    </template>

    <BaseButton
      v-else-if="!hideAfterPurchase && purchaseDone"
      style-type="tertiary"
      class="mt-2 w-full"
      @click="emit('hide')"
    >
      Close
    </BaseButton>
  </div>
</template>

<style scoped>

</style>